import { generateUUID } from '@ps/utils/game'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import {
  AstActionItem,
  AstActionParam,
  AstConditionItem,
  AstEventItem,
  paramsItem,
} from '@ps/bridge/qc/scripts/editor'
import _ from 'lodash'
import qc from 'qc'
import iframe from '@ps/bridge/iframe/iframe'
import generateId from '@/utils/generateId'
import {
  DragConditionType,
  getVpAstActionConfig,
  CustomEventType,
  DrawLineConditionType,
  EraseConditionType,
  VpMethods,
} from '@ps/bridge/qc/vp/config'
import store from '@ps/store'
import { getVpActionsFromNode } from '@ps/bridge/qc/vp/get-vp-actions-from-node'
import i18n from '@ps/lang'
import { scripts } from '@ps/bridge/qc/scripts'
import {
  getDrawLineInScene,
  getEraseNodeInScene,
  getCarouselNodeInScene,
  getMaskComponentNodeInScene,
  getSceneNodeByNode,
} from '@ps/bridge/qc/base'
import { pairItem } from '@ps/workbench/undo-redo/module/draw-line'
import { language } from '@/common/storage'
import nodePool from '@ps/bridge/qc/node-pool'
import { i18nParamParse } from './i18nParam'
import { i18nLayerName } from '@ps/views/workbench/layer-manager/layer'
import { isIvPage } from '@ps/router/config'
import { getBrainItemName } from '@ps/store/interactive-video/brain/brain'
export function makeCondition({
  uuid,
  conf,
}: {
  uuid: string
  conf: {
    command: string
    content?: string
    label: string
    type: 'event' | 'compare'
  }
}): AstConditionItem {
  const condition: AstConditionItem = {
    node: uuid,
    type: conf.type,
    conditionLabel: conf.label,
    content: conf.content,
  }
  if (
    conf.content === DragConditionType.VPDRAG_END ||
    conf.content === DragConditionType.VPUNDRAG_END
  ) {
    condition.node2 = generateUUID()
  }
  // 拖拽松手后默认复位
  if (isDragCondition(conf.content)) {
    condition.dragResetPos = true
  }
  return condition
}

export function makeCompareCondition({
  node,
  compareInfo,
}: {
  node: qc.Node
  compareInfo: paramsItem
}) {
  let compareData2
  if (compareInfo.compareData2Type === 'globalVar') {
    compareData2 = {
      targetNodeScript: 'ps.VPGamePlay',
      targetNode: store.state.workspace.game.gamePlay.uuid,
      data: compareInfo.compareData2,
    }
  } else {
    compareData2 = compareInfo.compareData2
  }
  return {
    type: 'compare',
    node: node.uuid,
    conditionLabel: i18n.t(
      'workbench.resourceManagers.categoryList.globalVar.conditionCompare',
    ),
    params: {
      compareData1: {
        targetNodeScript: 'ps.VPGamePlay',
        targetNode: store.state.workspace.game.gamePlay.uuid,
        data: compareInfo.compareData1,
      },
      compareMethod: compareInfo.compareMethod,
      compareData2Type: compareInfo.compareData2Type,
      compareData2,
    },
  }
}

export function getAstConfig(sceneId?: string): AstEventItem[] {
  const sceneList = iframe.gamePlay.children.filter((item) => {
    if (sceneId) {
      return sceneId === item.uuid
    }
    return true
  })
  return _.flatten(
    sceneList.map((scene) => {
      return editorScript.getVpAst(scene)
    }),
  )
}

/**
 * 获取 action
 * @param uuid 节点 id，如果有的话，则获取该节点对应的 action 配置
 */
export function getTargetNodeAstConfig(uuid: string): AstEventItem[] {
  const ast = getAstConfig()
  return editorScript.getTargetNodeAstConfig(uuid, ast)
}

export function getActions(
  ast: AstEventItem[],
  cb: (action: AstActionItem) => boolean,
): AstActionItem[] {
  const actions: AstActionItem[] = _.flatten(
    ast.map((item: AstEventItem) => item.action),
  )
  return actions.filter(cb)
}

export function useEventIdToFindEventIndex({
  id,
  sceneId,
}: {
  id: string
  sceneId: string
}): number {
  if (!sceneId || !id) {
    console.error('sceneId 与 id 是必需参数')
    return -1
  }
  // 不可以获取多个场景的 ast 配置去换算 id，因为每个场景的 ast 都是独立的
  const ast: AstEventItem[] = getAstConfig(sceneId)
  return _.findIndex(ast, (item) => item.id === id)
}

/**
 * 暂时用 any
 */
export function getActionEventConfig(
  action: AstActionItem,
  actionEventList: any,
) {
  for (const i in actionEventList) {
    const actionEvents = actionEventList[i]
    const actionEvent = actionEvents.find(
      (actionEvent: AstActionItem) =>
        actionEvent.script === action.script &&
        actionEvent.method === action.method,
    )
    if (actionEvent) {
      return _.cloneDeep(actionEvent)
    }
  }
}

function parseParamLabel(
  action: AstActionItem,
  actionParams: AstActionParam,
  actionEventConfig: AstActionItem,
) {
  if (actionEventConfig.paramParseFunc) {
    const actionI18nConfig = getVpAstActionConfig()
    let sceneNode = null
    if (action.node) {
      const node = nodePool.find(action.node)
      if (node) {
        sceneNode = getSceneNodeByNode(node)
      } else {
        sceneNode = null
      }
    }
    const lang = language.get()
    let res = actionEventConfig.paramParseFunc(
      actionParams,
      actionI18nConfig,
      sceneNode,
      lang,
    )
    res = i18nParamParse(action, res)
    if (isIvPage() && action.method === VpMethods.vpSwitchScene) {
      // 互动视频跳转场景用的是场景里的视频名称，这里记录id，UI层动态获取名称
      res = actionParams.jumpSceneId
    }
    return res
  }
  if (action.paramLabel) {
    return actionParams[action.paramLabel]
  }
  return ''
}

export function getParamLabelUI(targetList: string[]) {
  return targetList
    .map((item) => {
      const node = nodePool.find(item)
      const name = i18nLayerName(node.uuid, node.name)
      return name
    })
    .join(',')
}

/**
 * 加工 action，这是从旧代码迁移过来的，注意类型声明写的不够严谨
 */
export function makeAction({
  selectTarget,
  actionEvent,
  actionParams,
  actionEventConfig,
  targetList,
}: {
  selectTarget: qc.Node
  actionEvent: AstActionItem
  actionParams: AstActionParam
  actionEventConfig: AstActionItem
  targetList?: string[]
}): AstActionItem {
  actionParams = _.cloneDeep(actionParams)
  actionEvent = _.cloneDeep(actionEvent)
  const action: AstActionItem = {
    id: actionEvent.id,
    uuid: generateId(),
    targetLabel: selectTarget.name,
    label: actionEvent.label,
    node: selectTarget.uuid,
    method: actionEvent.method,
    script: actionEvent.script,
    target: actionEvent.target,
  }
  if (actionParams && Object.keys(actionParams).length) {
    action.param = actionParams
  }
  if (actionEvent.param && actionEvent.param.properties) {
    action.properties = actionEvent.param.properties
  }
  if (actionEvent.properties) {
    action.properties = actionEvent.properties
  }
  if (actionEvent.paramLabel) {
    action.paramLabel = actionEvent.paramLabel
  }
  if (action.paramLabel === 'target') {
    action.paramLabelUI = i18nLayerName(selectTarget.uuid, selectTarget.name)
  } else if (action.paramLabel === 'none') {
    action.paramLabelUI = ''
  } else {
    action.paramLabelUI = parseParamLabel(
      action,
      actionParams,
      actionEventConfig,
    )
  }
  if (targetList && targetList.length > 0) {
    action.targetList = targetList
    action.paramLabelUI = getParamLabelUI(targetList)
  }
  updateCarouselActionNode(action)
  updateDrawLineParamLabel(action)
  updateEraseActionNode(action)
  updateMaskActionNode(action)
  return action
}
export function updateDrawLineParamLabel(
  action: AstActionItem,
  sceneNode?: qc.Node,
) {
  // 连线组件不用选响应对象，自动设置为当前场景的唯一连线组件
  if (action.script === scripts.drawLine.name) {
    const selfSceneNode =
      sceneNode || store.state.workspace.game.sceneManager.currentSceneNode
    const drawLineNode = getDrawLineInScene(selfSceneNode)
    if (!drawLineNode) return
    action.node = drawLineNode.uuid
    if (action.param && action.param.pair && action.param.pair.length > 0) {
      const ped = drawLineNode?.getScript('playsmart.editor.data')
      if (!ped.$data.drawLineData) return
      const pairData = JSON.parse(ped.$data.drawLineData)
      const pairs: string[] = []
      const lang = language.get()
      pairData.forEach((item: pairItem) => {
        if (action.param.pair.indexOf(item.uuid) > -1) {
          const n = lang === 'zh-cn' ? item.name : item.nameEn
          pairs.push(n)
        }
      })
      action.paramLabelUI = pairs.join(',')
    }
    if (action.param && action.param.area && action.param.area.length > 0) {
      action.paramLabelUI = action.param.area
        .map(
          (area: string) =>
            i18n.t('workbench.drawLine.drawLineEdit.drawLineArea') + area,
        )
        .join(',')
    }
  }
}
// 擦除组件action的node自动设置成当前场景的擦除组件
export function updateEraseActionNode(
  action: AstActionItem,
  sceneNode?: qc.Node,
) {
  if (action.script === scripts.erase.name) {
    const selfSceneNode =
      sceneNode || store.state.workspace.game.sceneManager.currentSceneNode
    const eraseNode = getEraseNodeInScene(selfSceneNode)
    if (!eraseNode) return
    action.node = eraseNode.uuid
  }
}

// 轮播组件action的node自动设置成当前场景的擦除组件
export function updateCarouselActionNode(
  action: AstActionItem,
  sceneNode?: qc.Node,
) {
  if (action.script === scripts.carousel.name) {
    const selfSceneNode =
      sceneNode || store.state.workspace.game.sceneManager.currentSceneNode
    const carouselNode = getCarouselNodeInScene(selfSceneNode)
    if (!carouselNode) return
    action.node = carouselNode.uuid
  }
}

// 遮罩组件action的node自动设置成当前场景的遮罩组件
export function updateMaskActionNode(
  action: AstActionItem,
  sceneNode?: qc.Node,
) {
  if (action.script === scripts.mask.name) {
    const selfSceneNode =
      sceneNode || store.state.workspace.game.sceneManager.currentSceneNode
    const maskNode = getMaskComponentNodeInScene(selfSceneNode)
    if (!maskNode) return
    action.node = maskNode.uuid
  }
}

export function astItemFactory(condition: AstConditionItem): AstEventItem {
  const ast: AstEventItem = {
    id: generateUUID(),
    condition: [condition],
    action: [],
    disabled: false,
  }
  if (condition.content === CustomEventType.CUSTOM_EVENT) {
    ast.eventName = '自定义事件'
    ast.eventNameEn = 'Custom Event'
  }
  return ast
}

export function getActionsFromNode(node: qc.Node) {
  return getVpActionsFromNode(node, {
    hasBgm: Boolean(store.state.workspace.game.bgm.config.audioUrl),
  })
}

export function translateGlobalType(compareType: string) {
  let res: any = ''
  switch (compareType) {
    case 'number':
      res = i18n.t(
        'workbench.resourceManagers.categoryList.globalVar.typeNumber',
      )
      break
    case 'string':
      res = i18n.t(
        'workbench.resourceManagers.categoryList.globalVar.typeString',
      )
      break
    case 'boolean':
      res = i18n.t(
        'workbench.resourceManagers.categoryList.globalVar.typeBoolean',
      )
      break
    case 'globalVar':
      res = i18n.t(
        'workbench.resourceManagers.categoryList.globalVar.typeGlobalVar',
      )
      break
    default:
      res = ''
      break
  }
  return res
}
// 根据eventId获取一组eventItems
export function getEventItemGroupById(
  ast: AstEventItem[],
  eventId: string,
  isAll = false,
): AstEventItem[] {
  const eventAst = ast.filter((item) => item.id === eventId)
  if (eventAst.length === 0) {
    return []
  }
  let ansEventAst: AstEventItem[]
  // 特殊事件条件不用关联连带操作
  if (
    eventAst[0].condition[0].type === 'event' &&
    [
      CustomEventType.CUSTOM_EVENT,
      DrawLineConditionType.DRAW_START,
      DrawLineConditionType.DRAW_END,
      DragConditionType.VPDRAG_END,
      DragConditionType.VPUNDRAG_END,
      EraseConditionType.ERASE_REACH_STEP,
      EraseConditionType.ERASE_UN_REACH_STEP,
    ].indexOf(eventAst[0].condition[0].content) > -1
  ) {
    ansEventAst = eventAst
  } else if (eventAst[0].condition[0].type === 'event') {
    if (isAll) {
      ansEventAst = ast.filter(
        (item) =>
          item.condition[0] &&
          item.condition[0].type === 'event' &&
          item.condition[0].content === eventAst[0].condition[0].content &&
          item.condition[0].node === eventAst[0].condition[0].node,
      )
    } else {
      ansEventAst = eventAst
    }
  } else {
    if (isAll) {
      ansEventAst = ast.filter(
        (item) =>
          item.condition[0] &&
          item.condition[0].type === 'compare' &&
          item.condition[0].node === eventAst[0].condition[0].node,
      )
    } else {
      ansEventAst = eventAst
    }
  }
  return ansEventAst
}
// 区分是否有id映射的全局变量版本
export function hasPlaysmartGlobalVars(): boolean {
  return iframe.ps.VPGamePlay.__ability.indexOf('playsmartGlobalVars') > -1
}

// 区分是否支持沿x,y轴拖拽的版本
export function checkIsSupportDragDirection(): boolean {
  const supportAbility = iframe.ps.Drager.__ability || ''
  return supportAbility.indexOf('dragDirection') > -1
}
// 区分是否支持拖拽松手后复位的版本
export function checkIsSupportDragResetPos(): boolean {
  const supportAbility = iframe.ps.Drager.__ability || ''
  return supportAbility.indexOf('dragResetPos') > -1
}
// 区分是否支持拖拽范围的版本
export function checkIsSupportDragBound(): boolean {
  const supportAbility = iframe.ps.Drager.__ability || ''
  return supportAbility.indexOf('dragBound') > -1
}

export function checkHasAutoPlaySequence(): boolean {
  const supportAbility = iframe.ps.MovieClip.__ability || ''
  return supportAbility.indexOf('autoPlay') > -1
}

export function checkHasKeepLastFrameSequence(): boolean {
  const supportAbility = iframe.ps.MovieClip.__ability || ''
  return supportAbility.indexOf('keepLastFrame') > -1
}

// 区分是否有连线组件
export function checkIsSupportDrawLine(): boolean {
  try {
    const supportAbility = iframe.ps.VPGamePlay.__ability || ''
    return supportAbility.indexOf('drawLine') > -1
  } catch (e) {
    return false
  }
}

// 区分是否有自由画线组件
export function checkIsSupportFreeDrawLine(): boolean {
  try {
    const supportAbility = iframe.ps.VPGamePlay.__ability || ''
    return supportAbility.indexOf('freeDrawLine') > -1
  } catch (e) {
    return false
  }
}

// 是否有box2d能力
export function checkIsSupportBox2d(): boolean {
  try {
    const supportAbility = iframe.ps.VPGamePlay.__ability || ''
    return supportAbility.indexOf('box2d') > -1
  } catch (e) {
    return false
  }
}

// 区分是否有擦除组件
export function checkIsSupportErase(): boolean {
  try {
    const supportAbility = iframe.ps.VPGamePlay.__ability || ''
    return supportAbility.indexOf('erase') > -1
  } catch (e) {
    return false
  }
}

// 是否有组件库
export function hasWidgetLib(): boolean {
  try {
    const supportAbility = iframe.ps.VPGamePlay.__ability || ''
    const arr = supportAbility.split(',')
    const arr2 = arr.filter(
      (item: string) =>
        item !== 'playsmartGlobalVars' && item !== 'box2d' && item !== '',
    )
    return arr2.length > 0
  } catch (e) {
    console.log(e)
    return false
  }
}

// 区分是否有遮罩组件
export function checkIsSupportMask(): boolean {
  try {
    const supportAbility = iframe.ps.VPGamePlay.__ability || ''
    return supportAbility.indexOf('mask') > -1
  } catch (e) {
    return false
  }
}

// 区分是否支持轮播组件
export function checkIsSupportCarousel(): boolean {
  try {
    const supportAbility = iframe.ps.VPGamePlay.__ability || ''
    return supportAbility.indexOf('carousel') > -1
  } catch (e) {
    return false
  }
}

// 是否是拖拽类型的条件
export function isDragCondition(condition: string | undefined): boolean {
  if (!condition) return false
  const list = [
    DragConditionType.VPDRAG_START,
    DragConditionType.VPDRAG_END,
    DragConditionType.VPUNDRAG_END,
  ]
  return list.indexOf(condition) > -1
}
// 是否支持缩放动画横竖屏拆分
export function checkIsSupportTweenScaleSplit(): boolean {
  try {
    const supportAbility = iframe.ps.VerticalHorizontal.__ability || ''
    return supportAbility.indexOf('tweenScale') > -1
  } catch (e) {
    return false
  }
}
